import { useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, AlertCircle, CalendarRange } from 'lucide-react';
import { useActivationEvents, useCreateActivationEvent } from '@/hooks/useActivationEvents';
import { useOrganizations } from '@/hooks/useOrganizations';
import { EventStatusBadge } from '@/components/activations/statusBadges';

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export default function ActivationEventsPage() {
  const [, navigate] = useLocation();
  const { data: events, isLoading, isError, error, refetch } = useActivationEvents();
  const createMutation = useCreateActivationEvent();
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState<{ organizationId?: string; name?: string; slug?: string }>({});

  const filtered = useMemo(() => {
    const list = events ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (e) =>
        (e.name ?? '').toLowerCase().includes(q) ||
        (e.slug ?? '').toLowerCase().includes(q),
    );
  }, [events, search]);

  const resetForm = () => {
    setOrganizationId('');
    setName('');
    setNameAr('');
    setSlug('');
    setSlugTouched(false);
    setStartDate('');
    setEndDate('');
    setErrors({});
  };

  const handleCreate = async () => {
    const next: { organizationId?: string; name?: string; slug?: string } = {};
    if (!organizationId) next.organizationId = 'Please select an organization.';
    if (name.trim().length < 2) next.name = 'Name must be at least 2 characters.';
    if (!SLUG_RE.test(slug)) next.slug = 'Slug must be lowercase kebab-case (a-z, 0-9, hyphens).';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const created = await createMutation.mutateAsync({
      organizationId,
      name: name.trim(),
      nameAr: nameAr.trim() || null,
      slug,
      // Convert local date input to ISO-8601 UTC (platform DateTime convention).
      startDate: startDate ? new Date(startDate).toISOString() : null,
      endDate: endDate ? new Date(endDate).toISOString() : null,
    });
    setOpen(false);
    resetForm();
    navigate(`/activations/events/${created.id}`);
  };

  return (
    <div className="flex-1" data-testid="activation-events-page">
      <Header
        title="Activation Events"
        description="Create and manage activation events hosted by client organizations."
      >
        <Button onClick={() => setOpen(true)} data-testid="create-event-button">
          <Plus className="mr-2 h-4 w-4" />
          New event
        </Button>
      </Header>

      <div className="p-6">
        <div className="mb-4 max-w-sm">
          <Input
            placeholder="Search events…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="event-search"
          />
        </div>

        {isLoading && (
          <div className="space-y-2" data-testid="events-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}

        {isError && (
          <Alert variant="destructive" data-testid="events-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load events</AlertTitle>
            <AlertDescription className="flex items-center gap-3">
              {(error as Error)?.message || 'Something went wrong.'}
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <Card className="flex flex-col items-center gap-3 p-12 text-center" data-testid="events-empty">
            <CalendarRange className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No events match your search.' : 'No activation events yet.'}
            </p>
            {!search && (
              <Button onClick={() => setOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create your first event
              </Button>
            )}
          </Card>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dates</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <TableRow key={e.id} data-testid={`event-row-${e.id}`}>
                    <TableCell className="font-medium">
                      <Link
                        href={`/activations/events/${e.id}`}
                        className="hover:underline"
                        data-testid={`event-link-${e.id}`}
                      >
                        {e.name}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{e.slug}</TableCell>
                    <TableCell>
                      <EventStatusBadge status={e.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {e.startDate ? new Date(e.startDate).toLocaleDateString() : '—'}
                      {' → '}
                      {e.endDate ? new Date(e.endDate).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) resetForm();
        }}
      >
        <DialogContent data-testid="create-event-dialog">
          <DialogHeader>
            <DialogTitle>New activation event</DialogTitle>
            <DialogDescription>Select the client organization the event belongs to.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ev-org">Organization *</Label>
              <Select
                value={organizationId}
                onValueChange={(value) => {
                  setOrganizationId(value);
                  setErrors((prev) => ({ ...prev, organizationId: undefined }));
                }}
                disabled={orgsLoading}
              >
                <SelectTrigger id="ev-org" data-testid="event-organization">
                  <SelectValue placeholder={orgsLoading ? 'Loading organizations…' : 'Select an organization'} />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id} data-testid={`event-organization-option-${org.id}`}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.organizationId && (
                <p className="text-xs text-destructive" data-testid="event-organization-error">
                  {errors.organizationId}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-name">Name *</Label>
              <Input
                id="ev-name"
                data-testid="event-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
              />
              {errors.name && <p className="text-xs text-destructive" data-testid="event-name-error">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-name-ar">Name (Arabic)</Label>
              <Input id="ev-name-ar" data-testid="event-name-ar" dir="rtl" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-slug">Slug *</Label>
              <Input
                id="ev-slug"
                data-testid="event-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(slugify(e.target.value));
                }}
              />
              {errors.slug && <p className="text-xs text-destructive" data-testid="event-slug-error">{errors.slug}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ev-start">Start date</Label>
                <Input id="ev-start" data-testid="event-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ev-end">End date</Label>
                <Input id="ev-end" data-testid="event-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending} data-testid="submit-create-event">
              {createMutation.isPending ? 'Creating…' : 'Create event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
